export interface AuthenticationResult {
  success: boolean;
  token?: string;
  expiry?: string;
  message?: string;
  errors: string[];
}

export interface LoginRequest {
  Email: string;
  Password: string;
}

export interface RegisterRequest {
  Email: string;
  FirstName: string;
  LastName: string;
}

export interface UserProvisioningResult {
  success: boolean;
  userId?: string;
  email?: string;
  message?: string;
  errors: string[];
}

export interface ConfirmEmailRequest {
  UserId: string;
  Token: string;
  Password: string;
}

export interface EmailConfirmationResult {
  success: boolean;
  message?: string;
  errors: string[];
}

export interface ResendConfirmationRequest {
  Email: string;
}

// SC-296: self-service password reset.
export interface ForgotPasswordRequest {
  Email: string;
}

export interface ResetPasswordRequest {
  Token: string;
  NewPassword: string;
}

/** `errors` is non-empty only when the password was rejected; the link is then still valid. */
export interface PasswordResetResult {
  success: boolean;
  message?: string;
  errors: string[];
}

export interface ConfirmEmailChangeRequest {
  UserId: string;
  Token: string;
}

export interface UserInfo {
  userId: string;
  email: string;
  isAdmin: boolean;
  firstName?: string;
  lastName?: string;
  atlassianAccountId?: string;
  /** SC-238 tier; `isAdmin` is the Admin case of this. */
  role?: UserRole;
}

export enum ProductType {
  General = 0,
  CorePlatform = 1,
  ApiChangelog = 2,
  Portal = 3,
  SalesforceApp = 4,
  TransactionMonitoring = 5,
}

// --- Admin panel ---

/** The three SC-238 permission tiers. */
export type UserRole = 'ReadOnly' | 'General' | 'Admin';

export const USER_ROLES: { value: UserRole; label: string; description: string }[] = [
  { value: 'Admin', label: 'Admin', description: 'Manages permissions and shared reference data' },
  { value: 'General', label: 'General', description: 'Everything except the admin-only actions' },
  { value: 'ReadOnly', label: 'Read Only', description: 'May only view; every change is refused' },
];

export interface AdminUser {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isAdmin: boolean;
  emailConfirmed: boolean;
  isActive: boolean;
  createdAt?: string;
  lastLoginAt?: string;
  /** SC-238 tier. Optional so an older server response still type-checks. */
  role?: UserRole;
}

export interface AdminStats {
  totalUsers: number;
  admins: number;
  activeUsers: number;
  pendingConfirmation: number;
  totalFeeds: number;
  activeFeeds: number;
  totalFeedItems: number;
}

export interface AdminRssFeed {
  id: number;
  name: string;
  url: string;
  description: string;
  productType: ProductType;
  isActive: boolean;
  lastFetched: string;
}

export interface CreateRssFeedRequest {
  name: string;
  url: string;
  description: string;
  productType: ProductType;
}

export interface UpdateRssFeedRequest {
  name: string;
  url: string;
  description: string;
  productType: ProductType;
  isActive: boolean;
}

export interface ProductTypeInfo {
  value: number;
  /** Stable technical identifier (the ProductType enum member name). Not for display. */
  name: string;
  description: string;
  /** User-facing label (SC-282). Optional so an older server response still type-checks. */
  displayName?: string;
}

export enum ComplexityLevel {
  Simple = "Simple",
  Routine = "Routine",
  Moderate = "Moderate",
  Complex = "Complex",
  Critical = "Critical",
}

export enum ImpactLevel {
  Low = "Low",
  Medium = "Medium",
  High = "High",
  Critical = "Critical",
}

export enum BusinessSegment {
  Technical = "Technical",
  CIB = "CIB",
  AM = "AM",
  AS = "AS",
  Corp = "Corp",
}

export const BUSINESS_SEGMENTS: BusinessSegment[] = [
  BusinessSegment.Technical,
  BusinessSegment.CIB,
  BusinessSegment.AM,
  BusinessSegment.AS,
  BusinessSegment.Corp,
];

export type SegmentSource = 'None' | 'Ai' | 'Manual';

/** Which tier produced an item's classification (RF-91). 'Default' is the classifier's
    no-verdict placeholder; it is only ever stored as classificationMetadata under a lowered
    ApplyThreshold, since at its 0.3 confidence it does not clear the shipped 0.6 and the item
    gets a pending suggestion instead. Kept in the union so an unexpected stored value still
    renders. */
export type ClassificationMethod = 'Default' | 'Keyword' | 'LLM' | 'Manual';

/** Read-only provenance for an item's classification: how sure, why, and by which tier (RF-91). */
export interface FeedItemClassificationDto {
  method: ClassificationMethod;
  /** 0-1, but the scale is per tier and the tiers are not comparable: LLM is the model's own
      high/medium/low bucketed to 0.9/0.7/0.5, Keyword is the top rule's margin over its rival,
      Manual is a flat 1. See classificationMethodMeta.confidenceMeaning. */
  confidence: number;
  reasoning: string;
  classifiedAt: string;
}

export interface FeedItemDto {
  id: number;
  rssFeedId: number;
  feedName: string;
  title: string;
  description: string;
  link: string;
  publishedDate: string;
  productType: ProductType;
  isRead: boolean;
  isStarred: boolean;
  readAt?: string;
  complexity?: ComplexityLevel;
  potentialImpact?: ImpactLevel;
  tags?: string[];
  yearWeek?: string;
  businessSegments?: BusinessSegment[];
  businessSegmentsSource?: SegmentSource;
  /** Names of clients this item is assigned to, shown on the item like tags/segments (SC-298). */
  clients?: string[];
  /** Manually-assigned Release Type value, or null when unset (SC-300). */
  releaseType?: string | null;
  /** How many versions this item has had; >1 means it was edited after publishing (SC-278). */
  versionCount?: number;
  /** True when the note has been removed from the source feed or marked deleted (SC-278). */
  isDeleted?: boolean;
  /** When the most recent version was captured (SC-278). */
  lastVersionedAt?: string | null;
  /** How the classification above was reached; absent when the item was never classified (RF-91). */
  classification?: FeedItemClassificationDto | null;
  /** Who the item is assigned to and by when (SC-423); null when unassigned. */
  assignment?: FeedItemAssignmentDto | null;
}

// --- Assign to User (SC-423) ---

/**
 * Mirrors FeedItemAssignmentRules.MaxNotesLength on the server. The client and server apply the
 * same field rules; see features/rss-feeds/lib/assignmentValidation.ts.
 */
export const FEED_ITEM_ASSIGNMENT_NOTES_MAX = 2000;

/** One user on an assignment. `isActive` is false for someone deactivated since being assigned. */
export interface FeedItemAssigneeDto {
  userId: string;
  fullName: string;
  isActive: boolean;
}

export interface FeedItemAssignmentDto {
  assignees: FeedItemAssigneeDto[];
  /** yyyy-MM-dd (a calendar day, no time or timezone). */
  reviewByDate: string;
  notes?: string | null;
  updatedAt?: string;
}

/** Body of PUT /feeditems/{id}/assignment. All empty de-links the item. */
export interface SetFeedItemAssignmentRequest {
  userIds: string[];
  reviewByDate: string | null;
  notes: string | null;
  /**
   * When true the API answers 409 unless the item's assignment is still the one read: none when
   * `expectedUpdatedAt` is null, else the one saved at that `updatedAt`. Stops a stale modal from
   * silently overwriting or clearing someone else's assignment.
   */
  checkUnchanged?: boolean;
  expectedUpdatedAt?: string | null;
}

/** GET /users/lookup - open to every signed-in user, unlike /admin/users. */
export interface UserLookupDto {
  id: string;
  fullName: string;
  /**
   * Only with `activeOnly=true` (the Assign to User modal, which shows and searches it). The
   * everyone-included filter list leaves the property out, so deactivated users' addresses are
   * not handed to every signed-in user.
   */
  email?: string;
  isActive: boolean;
}

/** The feed's Review by Date filter. Sent by name; the server binds enums by name. */
export type ReviewStatus = 'NotYetDue' | 'Overdue';

export const REVIEW_STATUS_OPTIONS: { value: ReviewStatus; label: string; description: string }[] = [
  { value: 'NotYetDue', label: 'Not yet due', description: 'Review by Date is today or later' },
  { value: 'Overdue', label: 'Overdue', description: 'Review by Date has passed' },
];

/** A snapshot of a feed item's content at one point in time (SC-278). */
export interface FeedItemVersionDto {
  versionNumber: number;
  title: string;
  description: string;
  publishedDate: string;
  capturedAt: string;
}

export interface GetFeedItemsResult {
  items: FeedItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface DashboardSummary {
  totalItems: number;
  /** SC-421: published on the requested day, with the feed list's own date bounds. */
  itemsToday: number;
  itemsThisWeek: number;
  classifiedItems: number;
  // SC-421: tile counts, corpus-wide, each with the exact filter its card applies on click.
  unreadItems: number;
  starredItems: number;
  /** Complexity = Critical. */
  criticalItems: number;
  /** Potential impact = High. */
  highImpactItems: number;
  unclassifiedItems: number;
  itemsNeedingReview: number;
  autoClassifiedItems: number;
  averageClassificationConfidence: number;
}

export interface DashboardViewModel {
  summary: DashboardSummary;
}

export interface GetFeedItemsQuery {
  userId?: number;
  productType?: ProductType;
  startDate?: string;
  endDate?: string;
  isRead?: boolean;
  isStarred?: boolean;
  searchTerm?: string;
  page?: number;
  pageSize?: number;
  segments?: BusinessSegment[];
  /** SC-242: multi-select tag filter, matching on "any of". */
  tags?: string[];
  /** SC-242: multi-select client filter, matching items assigned to any of these clients. */
  clientIds?: number[];
  /** SC-234: backs the dashboard's Critical / High Impact cards. */
  complexity?: ComplexityLevel;
  impact?: ImpactLevel;
  /** SC-300/SC-301: filter to items assigned this Release Type value. */
  releaseType?: string;
  /** SC-278/SC-301: only items with more than one version. */
  multiVersion?: boolean;
  /** SC-278/SC-301: only deleted items; omitted hides deleted items. */
  deleted?: boolean;
  /** SC-423: items assigned to any of these users (repeated `assignedUserId` parameter). */
  assignedUserId?: string[];
  /** SC-423: Review by Date bucket, judged on `today`. */
  reviewStatus?: ReviewStatus;
  /** SC-423: the viewer's local day (yyyy-MM-dd) that `reviewStatus` is judged against. */
  today?: string;
}

/** SC-300: an admin-configurable "Release Type" metadata value. */
export interface ReleaseTypeDto {
  id: number;
  name: string;
  description?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string | null;
}

/**
 * Outcome of the SC-235 description backfill. Idempotent — a second run reports
 * `itemsUpdated: 0`.
 */
export interface BackfillDescriptionsResult {
  feedsProcessed: number;
  itemsExamined: number;
  itemsUpdated: number;
  itemsUnchanged: number;
  itemsNotStored: number;
  errors: string[];
  /** Client-side: the X-Trace-Id of the request, for the "View in Logs" deep link. */
  traceId?: string;
  /** Client-side: ISO time just before the request was sent (minus a small skew allowance). */
  startedAt?: string;
}

/** A tag and the line describing when it applies (SC-272). */
export interface TagOption {
  name: string;
  description: string;
  /** Chip colour, `#RRGGBB` (SC-376). */
  color?: string;
  /** Display order in pickers and filters (SC-376). */
  sortOrder?: number;
}

/** A tag as the admin Tags tab sees it (SC-376), including removed (inactive) ones. */
export interface AdminTag {
  id: number;
  name: string;
  description: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  /** Items carrying the tag; null on responses that don't recount. */
  itemCount?: number | null;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface TagInput {
  name: string;
  description: string;
  color: string;
}

/**
 * An edit of an existing tag. `expectedUpdatedAt` is the tag's `updatedAt` (or `createdAt` if it
 * was never edited) as the form read it: the server answers 409 with `reason: 'stale'` when the
 * tag has changed since, instead of overwriting another admin's rename.
 */
export interface TagUpdateInput extends TagInput {
  isActive: boolean;
  expectedUpdatedAt?: string | null;
}

/** Minimal client shape the feed filter needs (SC-242). */
export interface ClientOption {
  id: number;
  name: string;
}

export interface MarkAsReadRequest {
  userId: string;
  isRead: boolean;
}

export interface MarkAsStarredRequest {
  userId: string;
  isStarred: boolean;
}

// Result types for API operations
export interface MarkItemAsReadResult {
  success: boolean;
  feedItemId: number;
  isRead: boolean;
  message?: string;
}

export interface MarkItemAsStarredResult {
  success: boolean;
  feedItemId: number;
  isStarred: boolean;
  message?: string;
}

export interface UpdateSegmentsRequest {
  segments: BusinessSegment[];
}

export interface UpdateSegmentsResult {
  success: boolean;
  feedItemId: number;
  segments: BusinessSegment[];
  source: SegmentSource;
  message?: string;
}

export interface ProcessRssFeedsResult {
  feedsProcessed: number;
  newItemsAdded: number;
  /** Existing notes Fenergo edited during this run — each gained a new version (SC-278). */
  itemsUpdated: number;
  /** Notes that vanished from their feed and were archived during this run (SC-278). */
  itemsArchived: number;
  errors: string[];
  newItemIds: number[];
  /** Client-side: the X-Trace-Id of the request, for the "View in Logs" deep link. */
  traceId?: string;
  /** Client-side: ISO time just before the request was sent (minus a small skew allowance). */
  startedAt?: string;
}

export interface FeedItemClassificationStatus {
  id: number;
  isClassified: boolean;
}

export interface ClassificationStatusResult {
  statuses: FeedItemClassificationStatus[];
  allClassified: boolean;
}

export interface UpdateUserProfileRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  atlassianAccountId?: string;
}

export interface UpdateUserProfileResult {
  success: boolean;
  user?: UserInfo;
  message?: string;
  errors?: string[];
  /**
   * True when the request changed the email address: a confirmation link was emailed to the new
   * address and the change only takes effect once that link is followed. The account keeps its
   * current email in the meantime.
   */
  emailConfirmationRequired?: boolean;
}

/**
 * How often a user receives email notifications about new RSS items. Matches the backend
 * NotificationFrequency enum, which serializes as a PascalCase string.
 */
export type NotificationFrequency = 'Immediate' | 'Daily' | 'Weekly';

export interface NotificationPreferences {
  emailNotifications: boolean;
  notificationFrequency: NotificationFrequency;
}

export interface UpdateNotificationPreferencesRequest {
  emailNotifications: boolean;
  notificationFrequency: NotificationFrequency;
}

export interface GetNotificationPreferencesResult {
  success: boolean;
  preferences?: NotificationPreferences;
  message?: string;
  errors?: string[];
}

export interface UpdateNotificationPreferencesResult {
  success: boolean;
  preferences?: NotificationPreferences;
  message?: string;
  errors?: string[];
}

/** The fields the export preview cards render (SC-255). */
export interface ExportPreviewItemDto {
  id: number;
  title: string;
  feedName: string;
  publishedDate: string;
  isRead: boolean;
  isStarred: boolean;
  /** SC-423: the assignment columns the export carries. Optional for older servers. */
  assignedUsers?: string[];
  reviewByDate?: string | null;
  assignmentNotes?: string | null;
}

export interface ExportPreviewDto {
  estimatedItemCount: number;
  filterSummary: string;
  /**
   * The first few items the export would actually contain (SC-255). Optional so a response
   * from a server that predates it still type-checks.
   */
  sampleItems?: ExportPreviewItemDto[];
}

// --- Useful links on feed items (SC-237 add, SC-275 view/edit, SC-276 delete) ---

/** Mirrors FeedItemLink.MaxDescriptionLength on the server; both are fixed by SC-237. */
export const FEED_ITEM_LINK_DESCRIPTION_MAX = 100;

export interface FeedItemLinkDto {
  id: number;
  feedItemId: number;
  userId: string;
  url: string;
  description: string;
  createdAt: string;
  updatedAt?: string | null;
  isEdited: boolean;
  authorName?: string | null;
}

export interface CreateFeedItemLinkRequest {
  feedItemId: number;
  url: string;
  description: string;
}

export interface UpdateFeedItemLinkRequest {
  url: string;
  description: string;
}

// --- Admin server-log viewer (GET /admin/logs) ---

/** Serilog levels, serialized by name. */
export type ServerLogLevel = 'Verbose' | 'Debug' | 'Information' | 'Warning' | 'Error' | 'Fatal';

export const SERVER_LOG_LEVELS: ServerLogLevel[] = ['Verbose', 'Debug', 'Information', 'Warning', 'Error', 'Fatal'];

/** One redacted CLEF event. Every string field is already redacted and capped server-side. */
export interface ServerLogEntry {
  /** Opaque, stable within the retained files; use only as a React key. */
  id: string;
  timestamp: string;
  level: ServerLogLevel;
  message: string;
  exception?: string | null;
  exceptionType?: string | null;
  sourceContext?: string | null;
  traceId?: string | null;
  spanId?: string | null;
  /** The request path with its query string stripped. */
  requestPath?: string | null;
  properties: Record<string, string>;
  messageTruncated: boolean;
  exceptionTruncated: boolean;
}

export type ServerLogStoppedReason = 'limit' | 'exhausted' | 'byteBudget' | 'timeout' | 'windowStart' | 'cursorExpired';

export interface ServerLogScan {
  bytesScanned: number;
  linesScanned: number;
  filesScanned: number;
  malformedLines: number;
  stoppedReason: ServerLogStoppedReason;
  oldestScanned?: string | null;
}

export interface ServerLogSource {
  available: boolean;
  message?: string | null;
  retentionDays: number;
}

/** Cursor-paged, newest first. There is deliberately no totalCount (it would need a full scan). */
export interface ServerLogPage {
  items: ServerLogEntry[];
  nextCursor?: string | null;
  scan: ServerLogScan;
  source: ServerLogSource;
}

export interface ServerLogQuery {
  minLevel?: ServerLogLevel;
  search?: string;
  /** ISO-8601, treated as UTC server-side. */
  from?: string;
  to?: string;
  /** 32-hex or W3C traceparent. */
  traceId?: string;
  /** SourceContext prefix. */
  source?: string;
  cursor?: string;
  /** 1..200, default 100. */
  limit?: number;
}
